import LockIcon from '@mui/icons-material/LockRounded';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useAppSelector } from '@/shared/model';
import { selectRoles } from '../../model/rolesSlice';
import { selectUsers } from '../../model/slice';
import type { Role } from '../../model/types';
import { RoleRulesPreview } from '../RoleRulesPreview';

const cellSx = {
  borderRight: '1px solid #DDDFE7',
  '&:first-of-type': { borderLeft: '1px solid #DDDFE7' },
} as const;

const lastRowCellSx = {
  ...cellSx,
  '&:first-of-type': {
    ...cellSx['&:first-of-type'],
    borderBottomLeftRadius: 8,
  },
  '&:last-of-type': { borderBottomRightRadius: 8 },
} as const;

interface Props {
  onRoleClick: (role: Role) => void;
}

export function RolesTable({ onRoleClick }: Readonly<Props>) {
  const roles = useAppSelector(selectRoles);
  const users = useAppSelector(selectUsers);

  const userCountByRoleId = useMemo(() => {
    const counts: Record<string, number> = {};
    (users ?? []).forEach((user) => {
      counts[user.roleId] = (counts[user.roleId] ?? 0) + 1;
    });
    return counts;
  }, [users]);

  const visibleRoles = roles ?? [];

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          overflow: 'auto',
          borderRadius: 2,
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  borderTop: '1px solid #DDDFE7',
                  borderRight: '1px solid #DDDFE7',

                  '&:first-of-type': {
                    borderTopLeftRadius: 8,
                    borderLeft: '1px solid #DDDFE7',
                  },

                  '&:last-of-type': {
                    borderTopRightRadius: 8,
                  },
                },
              }}
            >
              <TableCell sx={{ backgroundColor: '#F1F3F8' }}>Name</TableCell>
              <TableCell sx={{ backgroundColor: '#F1F3F8' }}>Users</TableCell>
              <TableCell sx={{ backgroundColor: '#F1F3F8' }}>
                Permissions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRoles.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={3}
                  sx={{
                    textAlign: 'center',
                    borderRight: '1px solid #DDDFE7',
                    borderLeft: '1px solid #DDDFE7',
                    borderEndEndRadius: 8,
                    borderEndStartRadius: 8,
                  }}
                >
                  No roles found
                </TableCell>
              </TableRow>
            )}
            {visibleRoles.map((role, index) => {
              const isLast = index === visibleRoles.length - 1;
              const sx = isLast ? lastRowCellSx : cellSx;
              return (
                <TableRow
                  key={role.id}
                  sx={{
                    cursor: 'pointer',

                    '&:hover td': { backgroundColor: '#F9FAFB' },
                  }}
                  onClick={() => onRoleClick(role)}
                >
                  <TableCell sx={sx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {role.name ?? '—'}
                      </Typography>
                      {role.organizationId === null && (
                        <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      )}
                    </Box>
                    {role.description && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                      >
                        {role.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={sx}>
                    {userCountByRoleId[role.id] ?? 0}
                  </TableCell>
                  <TableCell sx={sx}>
                    <RoleRulesPreview rules={role.rules} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
